import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ASSETS } from './assetManifest.js';
import { useGameState } from './hooks/useGameState.js';
import { useNotifications } from './hooks/useNotifications.js';
import { processBreeding } from './gameLogic/breedingLogic.js';
import { calculateExpansionCost, canBuyDragon, processDragonPurchase, processDragonSale } from './gameLogic/shopLogic.js';
import { isCompetitorUnlocked, initializeBattle, resolveRound, checkBattleEnd, opponentChooseAction } from './gameLogic/battleLogic.js';
import { getEncyclopediaUnlockCost } from './gameLogic/unlockLogic.js';
import { CONSTANTS } from './data.js';

import GameHeader from './components/GameHeader.jsx';
import TabNavigation from './components/TabNavigation.jsx';
import InventoryTab from './components/tabs/InventoryTab.jsx';
import ShopTab from './components/tabs/ShopTab.jsx';
import BreedingTab from './components/tabs/BreedingTab.jsx';
import EncyclopediaTab from './components/tabs/EncyclopediaTab.jsx';
import BattleTab from './components/tabs/BattleTab.jsx';
import IndexTab from './components/tabs/IndexTab.jsx';
import FusionReveal from './components/FusionReveal.jsx';
import BattleInterface from './components/BattleInterface.jsx';
import BattleResultScreen from './components/BattleResultScreen.jsx';
import SplashScreen from './components/SplashScreen.jsx';
import StartMenu from './components/StartMenu.jsx';
import InstructionsScreen from './components/InstructionsScreen.jsx';

function App() {
  const [appState, setAppState] = useState('splash'); // 'splash', 'startMenu', 'game'
  const [showInstructions, setShowInstructions] = useState(false);
  const { notification, notificationType, showNotification } = useNotifications();
  const gameState = useGameState(showNotification);

  const {
    coins, setCoins,
    keys, setKeys,
    playerDragons, setPlayerDragons,
    maxDragons, setMaxDragons,
    shopCosts, setShopCosts,
    unlockedDragons, setUnlockedDragons,
    unlockedRecipes, setUnlockedRecipes,
    coinAnimation,
    selectedBreedingDragons, setSelectedBreedingDragons,
    activeTab, setActiveTab,
    indexUnlockedDragons,
    penaltyInfo, setPenaltyInfo,
    fusionResult, setFusionResult,
    battleProgress, setBattleProgress,
    battle, setBattle,
    selectedCompetitor, setSelectedCompetitor,
    selectedBattleTeam, setSelectedBattleTeam,
    coinsPerSecond
  } = gameState;

  const handleExpandInventory = () => {
    const expansionCost = calculateExpansionCost(maxDragons);
    if (coins < expansionCost) {
        showNotification('Not enough coins to expand inventory.', 'error');
        return;
    }
    setCoins(c => c - expansionCost);
    setMaxDragons(m => m + CONSTANTS.INVENTORY_EXPANSION_SLOTS_PER_UPGRADE);
    showNotification(`Inventory expanded by ${CONSTANTS.INVENTORY_EXPANSION_SLOTS_PER_UPGRADE} slots!`, 'success');
  };

  const handleBuyDragon = (dragonName) => {
    const buyCheck = canBuyDragon(dragonName, coins, shopCosts, playerDragons, maxDragons);
    if (!buyCheck.canBuy) {
      showNotification(buyCheck.reason, 'error');
      return;
    }

    const result = processDragonPurchase(dragonName, coins, shopCosts, playerDragons);
    setCoins(result.newCoins);
    setPlayerDragons(result.newPlayerDragons);
    setShopCosts(result.newShopCosts);
    showNotification(result.message, 'success');
  };

  const handleSellDragon = (dragonId) => {
    const result = processDragonSale(dragonId, playerDragons, coins);
    if (!result) return;

    setCoins(result.newCoins);
    setPlayerDragons(result.newPlayerDragons);
    setSelectedBreedingDragons(prev => prev.filter(id => id !== dragonId));
    showNotification(`You sold your ${result.dragonName} for ${result.sellValue} coins.`, 'success');
  };

  const handleBreedingSelection = (dragonId) => {
    setSelectedBreedingDragons(prev => {
      if (prev.includes(dragonId)) {
        return prev.filter(id => id !== dragonId);
      } else if (prev.length < 2) {
        return [...prev, dragonId];
      } else {
        return [prev[1], dragonId];
      }
    });
  };

  const handleBreed = () => {
    const result = processBreeding(selectedBreedingDragons, playerDragons, coins);
    
    if (result.success) {
      setPlayerDragons(result.newDragons);
      setCoins(result.newCoins);
      setSelectedBreedingDragons([]);

      const isNew = result.result.type === 'new_dragon' && !indexUnlockedDragons.includes(result.result.name);
      setFusionResult({ ...result.result, isNew });

      if (result.newUnlock) {
        setUnlockedDragons(prev => prev.includes(result.newUnlock) ? prev : [...prev, result.newUnlock]);
      }
    } else {
      if (result.penalty) {
        setPenaltyInfo(result.penalty);
      }
      if (result.newDragons) {
        setPlayerDragons(result.newDragons);
      }
      if (result.newCoins !== undefined) {
        setCoins(result.newCoins);
      }
      setSelectedBreedingDragons([]);
      if (result.error) {
        showNotification(result.error, 'error');
      }
    }
  };

  const handleCloseFusionReveal = () => {
    if (!fusionResult) return;
    const { name, level, type } = fusionResult;
    if (type === 'level_up') {
      showNotification(`Your ${name} has reached Level ${level}!`, 'success');
    } else {
      showNotification(`Breeding successful! You got a ${name}!`, 'success');
    }
    setFusionResult(null);
  };

  const handleUnlockRecipeWithCoins = (dragonName) => {
    const cost = getEncyclopediaUnlockCost(dragonName);
    if (coins < cost) {
      showNotification('Not enough coins to unlock this recipe.', 'error');
      return;
    }

    setCoins(c => c - cost);
    setUnlockedRecipes(prev => [...prev, dragonName]);
    showNotification(`Recipe for ${dragonName} unlocked!`, 'success');
  };

  const handleUnlockRecipeWithKey = (dragonName) => {
    if (keys < 1) {
      showNotification('Not enough keys to unlock this recipe.', 'error');
      return;
    }

    setKeys(k => k - 1);
    setUnlockedRecipes(prev => [...prev, dragonName]);
    showNotification(`Recipe for ${dragonName} unlocked with a key!`, 'success');
  };

  const handleSelectCompetitor = (competitor) => {
    if (!isCompetitorUnlocked(competitor.id, battleProgress)) {
      showNotification('You must defeat the previous competitor first!', 'error');
      return;
    }
    
    setSelectedCompetitor(competitor);
    showNotification(`Selected ${competitor.name} as your opponent!`, 'info');
  };

  const handleGoToBattlePrep = () => {
    if (!selectedCompetitor) {
      showNotification('Please select a competitor first!', 'error');
      return;
    }
    if (playerDragons.length < 6) {
      showNotification('You need at least 6 dragons to battle!', 'error');
      return;
    }
    setBattle(prev => ({ ...prev, phase: 'preparation' }));
  };

  const handleCancelBattlePrep = () => {
    setBattle(prev => ({ ...prev, phase: 'map' }));
    setSelectedBattleTeam([]);
  };

  const handleSelectBattleDragon = (dragonId) => {
    setSelectedBattleTeam(prev => {
      if (prev.includes(dragonId)) {
        return prev.filter(id => id !== dragonId);
      }
      if (prev.length < 6) {
        return [...prev, dragonId];
      }
      return prev;
    });
  };

  const handleStartBattle = () => {
    if (selectedBattleTeam.length !== 6) {
      showNotification('You must select exactly 6 dragons for your team.', 'error');
      return;
    }
    const team = selectedBattleTeam.map(id => playerDragons.find(d => d.id === id));
    const battleState = initializeBattle(team, selectedCompetitor);
    setBattle(battleState);
    showNotification(`Battle with ${selectedCompetitor.name} starting!`, 'info');
  };

  const handleEndBattle = (result) => {
    setBattle(prev => ({ ...prev, phase: 'result', result, initialCoins: coins }));
    if (result === 'victory') {
      setKeys(k => k + CONSTANTS.BATTLE_REWARD_KEYS);
      setBattleProgress(prev => {
        const newProgress = [...prev];
        const competitorIndex = battle.opponent.id - 1;
        if (newProgress[competitorIndex] < CONSTANTS.BATTLE_WINS_REQUIRED) {
          newProgress[competitorIndex]++;
        }
        return newProgress;
      });
      showNotification(`You are victorious! You earned ${CONSTANTS.BATTLE_REWARD_KEYS} keys.`, 'success');
    } else if (result === 'tie') {
      showNotification('The battle ended in a tie! No rewards or penalties.', 'info');
    } else {
      const penalty = Math.floor(coins * CONSTANTS.BATTLE_LOSS_PENALTY_PERCENTAGE);
      setCoins(c => c - penalty);
      showNotification(`You have been defeated. You lost ${penalty.toLocaleString()} coins.`, 'error');
    }
  };

  const processNextTurn = (currentBattleState) => {
    const battleResult = checkBattleEnd(currentBattleState.round, currentBattleState.playerScore, currentBattleState.opponentScore);
    if (battleResult) {
      handleEndBattle(battleResult);
      return;
    }

    if (currentBattleState.turn === 'opponent') {
      setTimeout(() => {
        const action = opponentChooseAction(currentBattleState.opponentTeam);
        setBattle(prev => ({
          ...prev,
          opponentAction: action,
          log: [...prev.log, `${prev.opponent.name} is choosing a stat...`]
        }));
      }, 1500);
    }
  };

  const handleEndFaceOff = () => {
    const { faceOff } = battle;
    if (!faceOff) return;

    const { result: winner, playerDragon, opponentDragon, stat, playerStat, opponentStat, tieBreaker } = faceOff;
    
    let logMessage;
    const baseLog = `Round ${battle.round}: Player's ${playerDragon.name} (${stat}: ${playerStat}) vs ${opponentDragon.name} (${stat}: ${opponentStat}).`;
    
    if (winner === 'player') {
      logMessage = `${baseLog} Player wins${tieBreaker ? ' on tie-breaker!' : '!'}`;
    } else if (winner === 'opponent') {
      logMessage = `${baseLog} Opponent wins${tieBreaker ? ' on tie-breaker!' : '!'}`;
    } else if (winner === 'double_tie') {
      logMessage = `${baseLog} Double tie! Both players get a point.`;
    } else {
      logMessage = `${baseLog} It's a tie!`; // Should not happen
    }

    const isPlayerTurnNext = battle.turn === 'opponent';

    const newBattleState = {
      ...battle,
      phase: 'active',
      faceOff: null,
      round: isPlayerTurnNext ? battle.round + 1 : battle.round,
      playerScore: battle.playerScore + (winner === 'player' || winner === 'double_tie' ? 1 : 0),
      opponentScore: battle.opponentScore + (winner === 'opponent' || winner === 'double_tie' ? 1 : 0),
      playerTeam: battle.playerTeam.map(d => d.id === playerDragon.id ? { ...d, used: true } : d),
      opponentTeam: battle.opponentTeam.map(d => d.id === opponentDragon.id ? { ...d, used: true, revealed: true } : d),
      log: [...battle.log, logMessage],
      turn: isPlayerTurnNext ? 'player' : 'opponent',
      opponentAction: isPlayerTurnNext ? null : battle.opponentAction,
    };
    setBattle(newBattleState);
    processNextTurn(newBattleState);
  };

  const handlePlayerAction = (dragonId, stat) => {
    const playerDragon = battle.playerTeam.find(d => d.id === dragonId);
    const opponentDragon = battle.opponentTeam.find(d => !d.used); // Simple: picks first available

    const roundResult = resolveRound(playerDragon, opponentDragon, stat);
    
    setBattle(prev => ({
      ...prev,
      phase: 'face-off',
      faceOff: { 
        playerDragon, 
        opponentDragon, 
        stat, 
        result: roundResult.winner, 
        playerStat: roundResult.playerStat, 
        opponentStat: roundResult.opponentStat,
        tieBreaker: roundResult.tieBreaker,
        playerTotalStats: roundResult.playerTotalStats,
        opponentTotalStats: roundResult.opponentTotalStats,
      }
    }));
  };

  const handlePlayerCounter = (dragonId) => {
    const playerDragon = battle.playerTeam.find(d => d.id === dragonId);
    const { dragon: opponentDragon, stat } = battle.opponentAction;

    const roundResult = resolveRound(playerDragon, opponentDragon, stat);

    setBattle(prev => ({
      ...prev,
      phase: 'face-off',
      faceOff: { 
        playerDragon, 
        opponentDragon, 
        stat, 
        result: roundResult.winner, 
        playerStat: roundResult.playerStat, 
        opponentStat: roundResult.opponentStat,
        tieBreaker: roundResult.tieBreaker,
        playerTotalStats: roundResult.playerTotalStats,
        opponentTotalStats: roundResult.opponentTotalStats,
      },
      log: [...prev.log, `${prev.opponent.name} chose ${stat}.`]
    }));
  };

  const handleContinueAfterBattle = () => {
    setBattle({ phase: 'map' });
    setSelectedBattleTeam([]);
    setSelectedCompetitor(null);
  };

  const handleRetryBattle = () => {
    setBattle({ phase: 'preparation', opponent: battle.opponent });
  };

  const renderContent = () => {
    if (battle.phase === 'active' || battle.phase === 'face-off') {
      return <BattleInterface battle={battle} onPlayerAction={handlePlayerAction} onPlayerCounter={handlePlayerCounter} onFaceOffEnd={handleEndFaceOff} />;
    }

    switch (activeTab) {
      case 'inventory':
        return (
          <InventoryTab
            playerDragons={playerDragons}
            maxDragons={maxDragons}
            coins={coins}
            coinsPerSecond={coinsPerSecond}
            onExpandInventory={handleExpandInventory}
            onSellDragon={handleSellDragon}
          />
        );
      case 'shop':
        return (
          <ShopTab
            unlockedDragons={unlockedDragons}
            shopCosts={shopCosts}
            coins={coins}
            playerDragons={playerDragons}
            maxDragons={maxDragons}
            onBuyDragon={handleBuyDragon}
          />
        );
      case 'breeding':
        return (
          <BreedingTab
            playerDragons={playerDragons}
            selectedBreedingDragons={selectedBreedingDragons}
            onBreedingSelection={handleBreedingSelection}
            onBreed={handleBreed}
          />
        );
      case 'encyclopedia':
        return (
          <EncyclopediaTab
            unlockedRecipes={unlockedRecipes}
            coins={coins}
            keys={keys}
            onUnlockRecipeWithCoins={handleUnlockRecipeWithCoins}
            onUnlockRecipeWithKey={handleUnlockRecipeWithKey}
          />
        );
      case 'battle':
        return (
          <BattleTab
            battleProgress={battleProgress}
            selectedCompetitor={selectedCompetitor}
            playerDragons={playerDragons}
            onSelectCompetitor={handleSelectCompetitor}
            onGoToBattlePrep={handleGoToBattlePrep}
            battle={battle}
            onCancelBattlePrep={handleCancelBattlePrep}
            selectedBattleTeam={selectedBattleTeam}
            onSelectBattleDragon={handleSelectBattleDragon}
            onStartBattle={handleStartBattle}
          />
        );
      case 'index':
        return (
          <IndexTab
            indexUnlockedDragons={indexUnlockedDragons}
          />
        );
      default:
        return null;
    }
  };

  if (appState === 'splash') {
    return <SplashScreen onComplete={() => setAppState('startMenu')} />;
  }

  const backgroundMap = {
    inventory: ASSETS.backgrounds.lair.path,
    shop: ASSETS.backgrounds.market.path,
    breeding: ASSETS.backgrounds.altar.path,
    encyclopedia: ASSETS.backgrounds.library.path,
    battle: ASSETS.backgrounds.battleArena.path,
    index: ASSETS.backgrounds.tome.path,
    activeBattle: ASSETS.backgrounds.battleBg.path,
  };

  let bgKey = activeTab;
  if (appState === 'startMenu') {
    bgKey = 'mythical';
  } else if (battle.phase === 'preparation') {
    bgKey = 'battle';
  } else if (battle.phase === 'active' || battle.phase === 'face-off' || battle.phase === 'result') {
    bgKey = 'activeBattle';
  }

  return (
    <div className="min-h-screen text-gray-200 font-sans">
      <div className="fixed inset-0 -z-10">
        {Object.entries(backgroundMap).map(([tab, path]) => (
          <div
            key={tab}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out"
            style={{ 
              backgroundImage: `url(${path})`,
              opacity: bgKey === tab ? 1 : 0,
            }}
          />
        ))}
        <div 
          className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out"
          style={{
            backgroundImage: `url(${ASSETS.backgrounds.mythical.path})`,
            opacity: !backgroundMap[bgKey] ? 1 : 0
          }}
        />
        <div className="absolute inset-0 bg-black/50"></div>
      </div>

      <div className="h-screen bg-transparent overflow-hidden">
        <div className="max-w-7xl mx-auto p-4 h-full flex flex-col">
          {appState === 'startMenu' ? (
            <StartMenu 
              onStartGame={() => setAppState('game')}
              onShowInstructions={() => setShowInstructions(true)}
            />
          ) : (
            <>
              <GameHeader 
                coins={coins}
                keys={keys}
                coinsPerSecond={coinsPerSecond}
                coinAnimation={coinAnimation}
              />

              {battle.phase !== 'active' && battle.phase !== 'face-off' && (
                <TabNavigation 
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  playerDragons={playerDragons}
                  maxDragons={maxDragons}
                />
              )}
              <main className="flex-1 min-h-0">
                {renderContent()}
              </main>
            </>
          )}

          {/* Notification */}
          <AnimatePresence>
            {notification && (
              <motion.div 
                className={`fixed top-5 right-5 px-6 py-3 rounded-lg shadow-2xl z-50 border-l-4 backdrop-blur-md ${
                  notificationType === 'success' ? 'bg-green-500/30 border-green-400' :
                  notificationType === 'error' ? 'bg-red-500/30 border-red-400' :
                  'bg-blue-500/30 border-blue-400'
                }`}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 100 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full animate-pulse ${
                    notificationType === 'success' ? 'bg-green-300' :
                    notificationType === 'error' ? 'bg-red-300' :
                    'bg-blue-300'
                  }`}></div>
                  <span className="font-semibold text-sm text-gray-100">{notification}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Penalty Overlay */}
          <AnimatePresence>
            {penaltyInfo && (
              <motion.div 
                className="fixed inset-0 bg-black/80 backdrop-blur-lg flex items-center justify-center z-50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                  <motion.div 
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.5, opacity: 0 }}
                      className="bg-gray-900/80 border-2 border-red-500/50 rounded-2xl p-8 text-center max-w-md shadow-2xl shadow-red-500/20"
                  >
                      <h2 className="text-3xl font-mono font-bold text-red-400 mb-4 text-glow">{penaltyInfo.title}</h2>
                      <p className="text-gray-300 mb-6">{penaltyInfo.message}</p>
                      <button 
                          onClick={() => setPenaltyInfo(null)}
                          className="btn btn-danger px-8 py-3"
                      >
                          Continue
                      </button>
                  </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Fusion Reveal Overlay */}
          <AnimatePresence>
            {fusionResult && (
              <FusionReveal fusionResult={fusionResult} onClose={handleCloseFusionReveal} />
            )}
          </AnimatePresence>

          {/* Battle Result Overlay */}
          <AnimatePresence>
            {battle.phase === 'result' && (
              <BattleResultScreen
                battle={battle}
                onContinue={handleContinueAfterBattle}
                onRetry={handleRetryBattle}
              />
            )}
          </AnimatePresence>

          {/* Instructions Overlay */}
          <AnimatePresence>
            {showInstructions && (
              <InstructionsScreen onClose={() => setShowInstructions(false)} />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default App;
